package br.com.htms.inspeccaocampo.data.ocorrencias

import br.com.htms.inspeccaocampo.auth.AuthApi
import br.com.htms.inspeccaocampo.data.JsonMap
import br.com.htms.inspeccaocampo.data.efetivo.Datas
import br.com.htms.inspeccaocampo.platform.currentTimeMillis

data class TipoOcorrencia(
    val prefixo: String,
    val nome: String,
    val tipoAcid: String,
    val ativo: Boolean,
    val proximoId: String
) {
    val rotulo: String get() = "$prefixo — $nome"
    val ehAcidente: Boolean get() = nome.contains("acidente", ignoreCase = true)
}

data class FotoOcorrencia(val id: String, val legenda: String, val createdAt: Long, val createdBy: String)

data class Ocorrencia(
    val id: String,
    val prefixo: String,
    val dados: Map<String, String>,
    val fotos: List<FotoOcorrencia>,
    val createdAt: Long,
    val createdBy: String,
    val updatedAt: Long,
    val updatedBy: String
) {
    fun campo(k: String): String = dados[k].orEmpty().trim()
    val data: String get() = campo("data")
    val tipo: String get() = campo("tipo")
    val colaborador: String get() = campo("colaborador")
    val local: String get() = campo("local")
    val statusInv: String get() = campo("statusInv")
    val ano: String get() = data.take(4)
    val ehAcidente: Boolean get() = ehAcidenteAtual()
    val investigacaoConcluida: Boolean get() = statusInv.startsWith("conclu", ignoreCase = true)

    fun marcasCaracterizacao(): List<String> =
        campo("caracterizacao").split(",").map { it.trim() }.filter { it.isNotEmpty() }

    fun classeRotulo(): String = nomeClasseOc(campo("classificacao"))

    /** Acidente pela caracterização, pela classe 3–5 ou pelo nome do tipo (catálogo ou campo legado). */
    fun ehAcidenteAtual(nomeDoTipo: String = ""): Boolean {
        if (nomeDoTipo.contains("acidente", true) || tipo.contains("acidente", true)) return true
        if (marcasCaracterizacao().any { it.contains("acidente", true) }) return true
        return digitoClasseOc(campo("classificacao")) in setOf("3", "4", "5")
    }

    fun comAfastamento(): Boolean =
        marcasCaracterizacao().any { it.equals("Acidente com afastamento", true) }

    /** Investigação ainda aberta depois de 7 dias da data da ocorrência. */
    fun investigacaoForaDoPrazo(hoje: String): Boolean {
        if (investigacaoConcluida || !Datas.valida(data) || data > hoje) return false
        return Datas.diff(hoje, data) > 7
    }

    fun contem(busca: String): Boolean {
        val b = busca.trim()
        if (b.isEmpty()) return true
        return id.contains(b, true) || colaborador.contains(b, true) || local.contains(b, true) ||
            tipo.contains(b, true) || campo("descricao").contains(b, true) || campo("matricula").contains(b, true) ||
            campo("obra").contains(b, true) || campo("caracterizacao").contains(b, true) ||
            campo("classificacao").contains(b, true) || campo("empresa").contains(b, true)
    }
}

private val NOMES_CLASSE_OC = mapOf(
    "0" to "Classe 0 - Desvio / Condição Insegura",
    "1" to "Classe 1 - Quase Acidente",
    "2" to "Classe 2 - Incidente com Dano Material ou Ambiental",
    "3" to "Classe 3 - Acidente com Lesão Leve",
    "4" to "Classe 4 - Acidente Grave",
    "5" to "Classe 5 - Acidente Gravíssimo/Fatal"
)

/** Número depois de "Classe". Sem Regex: o `(?i)` quebra no navegador. */
fun digitoClasseOc(raw: String): String? {
    val t = raw.lowercase()
    val i = t.indexOf("classe")
    if (i < 0) return null
    var j = i + 6
    while (j < t.length && !t[j].isDigit()) {
        val c = t[j]
        if (c != ' ' && c != '\t' && c != ':' && c != '-' && c != '—' && c != '.') return null
        j++
    }
    return if (j < t.length) t[j].toString() else null
}

fun nomeClasseOc(raw: String): String {
    val t = raw.trim()
    if (t.isEmpty()) return "Não classificada"
    return NOMES_CLASSE_OC[digitoClasseOc(t)] ?: t
}

data class AcaoPlano(val id: String, val ocId: String, val dados: Map<String, String>, val updatedAt: Long, val updatedBy: String) {
    fun campo(k: String): String = dados[k].orEmpty().trim()
    val concluida: Boolean get() = campo("status").startsWith("conclu", ignoreCase = true)
    fun vencida(hoje: String): Boolean {
        val p = campo("prazo")
        return !concluida && Datas.valida(p) && p < hoje
    }
}

data class FuncOcorrencia(
    val id: String,
    val nome: String,
    val matricula: String,
    val funcao: String,
    val admissao: String = "",
    val nascimento: String = ""
)

data class CentroOc(val codigo: String, val nome: String) {
    val rotulo: String get() = if (nome.isBlank()) codigo else "$codigo — $nome"
}

data class OcLog(val id: String, val createdAt: Long, val usuario: String, val nome: String, val acao: String, val ref: String, val txt: String)

data class OcorrenciasDados(
    val master: Boolean,
    val usuario: String,
    val nome: String,
    val tipos: List<TipoOcorrencia>,
    val ocorrencias: List<Ocorrencia>,
    val acoes: List<AcaoPlano>,
    val meta: Int?,
    val funcionarios: List<FuncOcorrencia>,
    val centros: List<CentroOc> = emptyList(),
    val podeEditar: Boolean = true,
    val podeExcluir: Boolean = false
) {
    fun tipo(prefixo: String): TipoOcorrencia? = tipos.firstOrNull { it.prefixo == prefixo }
    fun ocorrencia(id: String): Ocorrencia? = ocorrencias.firstOrNull { it.id == id }
    fun acoesDe(ocId: String): List<AcaoPlano> = acoes.filter { it.ocId == ocId }
}

/** Campo do formulário (mesmas chaves do backup "portal-ocorrencias-backup"). */
data class CampoOcorrencia(
    val chave: String,
    val rotulo: String,
    val linhas: Int = 1,
    val numero: Boolean = false,
    val data: Boolean = false,
    val sugestoes: List<String> = emptyList(),
    /** Várias marcas ao mesmo tempo, gravadas separadas por vírgula. */
    val multiplo: Boolean = false
)

data class SecaoOcorrencia(val titulo: String, val campos: List<CampoOcorrencia>)

object CamposOcorrencia {
    private val classes = listOf(
        "Classe 0 - Desvio / Condição Insegura",
        "Classe 1 - Quase Acidente",
        "Classe 2 - Incidente com Dano Material ou Ambiental",
        "Classe 3 - Acidente com Lesão Leve",
        "Classe 4 - Acidente Grave",
        "Classe 5 - Acidente Gravíssimo/Fatal"
    )
    private val relacao = listOf("Causa", "Efeito", "N/A")
    private val fatores = listOf(
        "material" to "Material",
        "metodo" to "Método",
        "maquina" to "Máquina",
        "maoObra" to "Mão de obra",
        "medida" to "Medida",
        "meioAmb" to "Meio ambiente"
    )

    private fun fator(chave: String, nome: String) = listOf(
        CampoOcorrencia("${chave}Rel", "$nome — relação (causa, efeito ou N/A)", sugestoes = relacao),
        CampoOcorrencia(chave, "$nome — itens a esclarecer", linhas = 2),
        CampoOcorrencia("${chave}Aval", "$nome — avaliação do ocorrido pelo TST", linhas = 2)
    )

    val secoes = listOf(
        SecaoOcorrencia(
            "1. Investigação de",
            listOf(
                CampoOcorrencia(
                    "caracterizacao", "Caracterização (pode marcar mais de uma)",
                    linhas = 2, multiplo = true,
                    sugestoes = listOf(
                        "Ocorrência",
                        "Incidente",
                        "Acidente ambiental",
                        "Acidente sem afastamento",
                        "Acidente com afastamento",
                        "Acidente c/ danos materiais"
                    )
                ),
                CampoOcorrencia("tipoAcid", "Típico / Trajeto", sugestoes = listOf("Típico", "Trajeto"))
            )
        ),
        SecaoOcorrencia(
            "2. Classificação do evento",
            listOf(CampoOcorrencia("classificacao", "Classificação", sugestoes = classes))
        ),
        SecaoOcorrencia(
            "3. Comissão estabelecida para investigação",
            (1..6).flatMap { n ->
                listOf(
                    CampoOcorrencia("com$n", "Participante $n"),
                    CampoOcorrencia("cargo$n", "Cargo / função $n")
                )
            } + CampoOcorrencia("comissao", "Comissão (texto livre)", linhas = 2)
        ),
        SecaoOcorrencia(
            "4. Dados do contrato",
            listOf(
                CampoOcorrencia("empresa", "Cliente", sugestoes = listOf("HTMS")),
                CampoOcorrencia("contrato", "Contrato"),
                CampoOcorrencia("obra", "Obra")
            )
        ),
        SecaoOcorrencia(
            "5. Dados do envolvido",
            listOf(
                CampoOcorrencia("colaborador", "Nome"),
                CampoOcorrencia("funcao", "Função"),
                CampoOcorrencia("tempoEmpresa", "Tempo de empresa"),
                CampoOcorrencia("idade", "Idade", numero = true),
                CampoOcorrencia(
                    "escolaridade", "Escolaridade",
                    sugestoes = listOf(
                        "Ensino fundamental incompleto",
                        "Ensino fundamental completo",
                        "Ensino médio incompleto",
                        "Ensino médio completo",
                        "Ensino superior incompleto",
                        "Ensino superior completo"
                    )
                ),
                CampoOcorrencia(
                    "estadoCivil", "Estado civil",
                    sugestoes = listOf("Solteiro", "Casado", "União estável", "Divorciado", "Viúvo")
                ),
                CampoOcorrencia("matricula", "Matrícula"),
                CampoOcorrencia("sexo", "Sexo", sugestoes = listOf("Masculino", "Feminino"))
            )
        ),
        SecaoOcorrencia(
            "6. Dados da ocorrência / acidente",
            listOf(
                CampoOcorrencia("data", "Data do acidente", data = true),
                CampoOcorrencia("hora", "Hora do acidente (hh:mm)"),
                CampoOcorrencia("horasTrab", "Horas trabalhadas"),
                CampoOcorrencia("local", "Local do acidente"),
                CampoOcorrencia("parteCorpo", "Parte do corpo lesionada", sugestoes = listOf("N/A")),
                CampoOcorrencia("agente", "Agente causador da lesão"),
                CampoOcorrencia("natureza", "Natureza da lesão", sugestoes = listOf("N/A", "Corte (laceração)")),
                CampoOcorrencia("diasAfast", "Dias de afastamento", numero = true),
                CampoOcorrencia("diasDeb", "Dias debitados", numero = true),
                CampoOcorrencia("atividade", "Atividade executada", linhas = 2)
            )
        ),
        SecaoOcorrencia(
            "7. Responsável pela atividade",
            listOf(
                CampoOcorrencia("respAtiv", "Responsável pela atividade"),
                CampoOcorrencia("cargoAtiv", "Cargo"),
                CampoOcorrencia("estavaLocal", "Estava no local", sugestoes = listOf("Sim", "Não"))
            )
        ),
        SecaoOcorrencia(
            "8. Entrevistas",
            listOf(
                CampoOcorrencia("entrevistaSup", "Entrevista com supervisor / encarregado", linhas = 4),
                CampoOcorrencia("testemunhas", "Entrevista com testemunhas", linhas = 4)
            )
        ),
        SecaoOcorrencia(
            "9. Descrição do evento",
            listOf(CampoOcorrencia("descricao", "Descrição do evento", linhas = 6))
        ),
        SecaoOcorrencia(
            "10. Perdas do evento",
            listOf(CampoOcorrencia("perdas", "Perdas do evento", linhas = 3))
        ),
        SecaoOcorrencia(
            "11. Análise das causas do evento",
            fatores.flatMap { (chave, nome) -> fator(chave, nome) } + listOf(
                CampoOcorrencia(
                    "causaRaiz", "Causa raiz",
                    sugestoes = listOf("Método", "Mão de obra", "Máquina", "Material", "Medida", "Meio ambiente")
                ),
                CampoOcorrencia("conclusaoAnalise", "Conclusão da análise", linhas = 4)
            )
        ),
        SecaoOcorrencia(
            "12. Conclusão da investigação (prazo máximo de 7 dias após a ocorrência)",
            listOf(
                CampoOcorrencia("barreiras", "Quais barreiras de segurança falharam para a ocorrência do evento?", linhas = 3),
                CampoOcorrencia("causaBarreira", "Qual a causa da falha das barreiras de segurança?", linhas = 3),
                CampoOcorrencia("dataInv", "Data da investigação", data = true),
                CampoOcorrencia("respInv", "Responsável pela investigação"),
                CampoOcorrencia("statusInv", "Status da investigação", sugestoes = listOf("Pendente", "Em andamento", "Concluida")),
                CampoOcorrencia("conclusao", "Conclusão da investigação", linhas = 4),
                CampoOcorrencia("licoes", "Lições aprendidas", linhas = 3),
                CampoOcorrencia("divulgado", "Divulgado", sugestoes = listOf("Sim", "Não"))
            )
        ),
        SecaoOcorrencia(
            "Observações / recomendações",
            listOf(CampoOcorrencia("obs", "Observações / recomendações", linhas = 3))
        )
    )

    val acao = listOf(
        CampoOcorrencia("problema", "Problema", linhas = 2),
        CampoOcorrencia("acao", "Ação", linhas = 3),
        CampoOcorrencia("resp", "Responsável"),
        CampoOcorrencia("tipo", "Tipo da ação", sugestoes = listOf("Corretiva", "Preventiva")),
        CampoOcorrencia("prazo", "Data prevista", data = true),
        CampoOcorrencia("dataConc", "Data realizada", data = true),
        CampoOcorrencia("status", "Status", sugestoes = listOf("Pendente", "Em andamento", "Concluida")),
        CampoOcorrencia("respVerif", "Responsável pela verificação"),
        CampoOcorrencia("dataVerif", "Data da verificação", data = true),
        CampoOcorrencia("implantada", "Ação implantada", sugestoes = listOf("Sim", "Não")),
        CampoOcorrencia("eficacia", "Ação é eficaz", sugestoes = listOf("Sim", "Não")),
        CampoOcorrencia("evidencia", "Evidência / comentários", linhas = 3)
    )

    fun rotulo(chave: String): String =
        (secoes.flatMap { it.campos } + acao).firstOrNull { it.chave == chave }?.rotulo ?: chave
}

object OcDatas {
    fun hoje(): String = Datas.ds((currentTimeMillis() - 3 * 3_600_000L) / 86_400_000L)

    /** "aaaa-mm-dd" -> "dd/mm/aaaa" (vazio se inválida). */
    fun br(iso: String): String = if (Datas.valida(iso)) Datas.br(iso) else iso

    /** Aceita "dd/mm/aaaa", "dd/mm/aa" ou ISO; null se inválida. */
    fun iso(txt: String): String? {
        val t = txt.trim()
        if (t.isEmpty()) return ""
        if (Datas.valida(t)) return t
        val p = t.split("/", "-", ".").map { it.trim() }
        if (p.size != 3) return null
        val d = p[0].toIntOrNull() ?: return null
        val m = p[1].toIntOrNull() ?: return null
        var a = p[2].toIntOrNull() ?: return null
        if (a < 100) a += 2000
        val s = "${a.toString().padStart(4, '0')}-${m.toString().padStart(2, '0')}-${d.toString().padStart(2, '0')}"
        return         if (Datas.valida(s)) s else null
    }

    /** Idade em anos completos a partir de dd/mm/aaaa ou ISO. */
    fun idade(nascimento: String, hoje: String = hoje()): String {
        val iso = iso(nascimento)?.takeIf { Datas.valida(it) } ?: return ""
        if (iso > hoje) return ""
        val (y1, m1, d1) = iso.split("-").map { it.toInt() }
        val (y2, m2, d2) = hoje.split("-").map { it.toInt() }
        var anos = y2 - y1
        if (m2 < m1 || (m2 == m1 && d2 < d1)) anos--
        return if (anos < 0) "" else anos.toString()
    }

    /** Tempo de casa a partir da admissão, no formato do relatório. */
    fun tempoEmpresa(admissao: String, hoje: String = hoje()): String {
        val iso = iso(admissao)?.takeIf { Datas.valida(it) } ?: return ""
        if (iso > hoje) return ""
        val (y1, m1, d1) = iso.split("-").map { it.toInt() }
        val (y2, m2, d2) = hoje.split("-").map { it.toInt() }
        var anos = y2 - y1
        var meses = m2 - m1
        var dias = d2 - d1
        if (dias < 0) {
            meses--
            val mesAnt = if (m2 == 1) 12 else m2 - 1
            val anoAnt = if (m2 == 1) y2 - 1 else y2
            dias += diasNoMes(anoAnt, mesAnt)
        }
        if (meses < 0) {
            anos--
            meses += 12
        }
        if (anos < 0) return ""
        val partes = mutableListOf<String>()
        if (anos > 0) partes += if (anos == 1) "1 ano" else "$anos anos"
        if (meses > 0) partes += if (meses == 1) "1 mês" else "$meses meses"
        if (dias > 0) partes += if (dias == 1) "1 dia" else "$dias dias"
        return when (partes.size) {
            0 -> "0 dias"
            1 -> partes[0]
            else -> partes.dropLast(1).joinToString(", ") + " e " + partes.last()
        }
    }

    private fun diasNoMes(ano: Int, mes: Int): Int {
        val ini = "${ano.toString().padStart(4, '0')}-${mes.toString().padStart(2, '0')}-01"
        val fim = if (mes == 12) "${ano + 1}-01-01" else "$ano-${(mes + 1).toString().padStart(2, '0')}-01"
        return Datas.diff(fim, ini).coerceAtLeast(28)
    }
}

object OcorrenciasApi {
    suspend fun dados(): Result<OcorrenciasDados> =
        AuthApi.apiJson("GET", "/ocorrencias/dados").mapCatching { text ->
            val o = JsonMap.parse(text)
            OcorrenciasDados(
                master = o.bool("master"),
                usuario = o.str("usuario"),
                nome = o.str("nome"),
                tipos = objs(o, "tipos").map {
                    TipoOcorrencia(it.str("prefixo"), it.str("nome"), it.str("tipoAcid"), it.bool("ativo", true), it.str("proximoId"))
                },
                ocorrencias = objs(o, "ocorrencias").map { r ->
                    Ocorrencia(
                        id = r.str("id"),
                        prefixo = r.str("prefixo"),
                        dados = r.obj("dados")?.stringValues().orEmpty(),
                        fotos = objs(r, "fotos").map {
                            FotoOcorrencia(it.str("id"), it.str("legenda"), it.long("createdAt") ?: 0L, it.str("createdBy"))
                        },
                        createdAt = r.long("createdAt") ?: 0L,
                        createdBy = r.str("createdBy"),
                        updatedAt = r.long("updatedAt") ?: 0L,
                        updatedBy = r.str("updatedBy")
                    )
                },
                acoes = objs(o, "acoes").map {
                    AcaoPlano(it.str("id"), it.str("ocId"), it.obj("dados")?.stringValues().orEmpty(), it.long("updatedAt") ?: 0L, it.str("updatedBy"))
                },
                meta = o.obj("config")?.str("meta")?.toIntOrNull(),
                funcionarios = objs(o, "funcionarios").map {
                    FuncOcorrencia(
                        it.str("id"), it.str("nome"), it.str("matricula"), it.str("funcao"),
                        it.str("admissao"), it.str("nascimento")
                    )
                },
                centros = objs(o, "centros").map { CentroOc(it.str("codigo"), it.str("nome")) },
                podeEditar = o.bool("podeEditar", true),
                podeExcluir = o.bool("podeExcluir")
            )
        }

    suspend fun log(ref: String): Result<List<OcLog>> =
        AuthApi.apiJson("GET", "/ocorrencias/log?ref=${enc(ref)}").mapCatching { text ->
            objs(JsonMap.parse(text), "logs").map {
                OcLog(it.str("id"), it.long("createdAt") ?: 0L, it.str("usuario"), it.str("nome"), it.str("acao"), it.str("ref"), it.str("txt"))
            }
        }

    /** Cria a ocorrência; o servidor gera o ID pelo prefixo do tipo. */
    suspend fun criar(prefixo: String, dados: Map<String, String>): Result<String> =
        AuthApi.apiJson("POST", "/ocorrencias", "{\"prefixo\":${jsonStr(prefixo)},\"dados\":${jsonObj(dados)}}")
            .mapCatching { JsonMap.parse(it).str("id").ifBlank { error("Resposta inválida do servidor") } }

    suspend fun atualizar(id: String, dados: Map<String, String>): Result<Unit> =
        AuthApi.apiJson("PUT", "/ocorrencias/${enc(id)}", "{\"dados\":${jsonObj(dados)}}").map { }

    suspend fun excluir(id: String): Result<Unit> =
        AuthApi.apiJson("DELETE", "/ocorrencias/${enc(id)}", "{}").map { }

    suspend fun enviarFoto(ocId: String, bytes: ByteArray, legenda: String): Result<Unit> {
        val tipo = if (bytes.size > 3 && bytes[0] == 0x89.toByte()) "image/png" else "image/jpeg"
        return AuthApi.apiUploadBytes("/ocorrencias/${enc(ocId)}/fotos?legenda=${enc(legenda)}", bytes, tipo).map { }
    }

    suspend fun baixarFoto(fotoId: String): Result<ByteArray> =
        AuthApi.apiDownloadBytes("/ocorrencias/fotos/${enc(fotoId)}")

    suspend fun excluirFoto(fotoId: String): Result<Unit> =
        AuthApi.apiJson("DELETE", "/ocorrencias/fotos/${enc(fotoId)}", "{}").map { }

    suspend fun criarTipo(prefixo: String, nome: String, tipoAcid: String): Result<Unit> =
        AuthApi.apiJson(
            "POST", "/ocorrencias/tipos",
            "{\"prefixo\":${jsonStr(prefixo)},\"nome\":${jsonStr(nome)},\"tipoAcid\":${jsonStr(tipoAcid)}}"
        ).map { }

    suspend fun atualizarTipo(
        prefixo: String,
        nome: String,
        tipoAcid: String,
        ativo: Boolean,
        novoPrefixo: String = prefixo
    ): Result<Unit> =
        AuthApi.apiJson(
            "PUT", "/ocorrencias/tipos/${enc(prefixo)}",
            "{\"nome\":${jsonStr(nome)},\"tipoAcid\":${jsonStr(tipoAcid)},\"ativo\":$ativo,\"novoPrefixo\":${jsonStr(novoPrefixo)}}"
        ).map { }

    /** [id] vazio cria um novo plano (PA-###). Retorna o ID. */
    suspend fun salvarAcao(id: String, ocId: String, dados: Map<String, String>): Result<String> {
        val corpo = "{\"ocId\":${jsonStr(ocId)},\"dados\":${jsonObj(dados)}}"
        val r = if (id.isBlank()) AuthApi.apiJson("POST", "/ocorrencias/acoes", corpo)
        else AuthApi.apiJson("PUT", "/ocorrencias/acoes/${enc(id)}", corpo)
        return r.mapCatching { JsonMap.parse(it).str("id").ifBlank { id } }
    }

    suspend fun excluirAcao(id: String): Result<Unit> =
        AuthApi.apiJson("DELETE", "/ocorrencias/acoes/${enc(id)}", "{}").map { }

    suspend fun salvarMeta(meta: Int): Result<Unit> =
        AuthApi.apiJson("POST", "/ocorrencias/config", "{\"meta\":$meta}").map { }

    /** Envia o conteúdo do arquivo de backup (.json); retorna um resumo legível. */
    suspend fun importar(backupJson: String): Result<String> =
        AuthApi.apiJson("POST", "/ocorrencias/importar", backupJson).mapCatching { text ->
            val o = JsonMap.parse(text)
            val novos = o.strList("tiposNovos")
            buildString {
                append("Backup lido: ${o.long("ocorrencias") ?: 0} ocorrências e ${o.long("acoes") ?: 0} planos de ação. ")
                append("Os que já existiam não foram alterados.")
                if (novos.isNotEmpty()) append(" Tipos criados: ${novos.joinToString(", ")}.")
            }
        }

    private fun objs(o: JsonMap, k: String): List<JsonMap> = o.array(k).orEmpty().mapNotNull { it as? JsonMap }

    private fun jsonObj(m: Map<String, String>): String =
        m.entries.joinToString(",", "{", "}") { (k, v) -> "${jsonStr(k)}:${jsonStr(v)}" }

    private fun jsonStr(s: String): String = buildString {
        append('"')
        for (c in s) {
            when (c) {
                '\\' -> append("\\\\")
                '"' -> append("\\\"")
                '\n' -> append("\\n")
                '\r' -> append("\\r")
                '\t' -> append("\\t")
                else -> if (c < ' ') append("\\u").append(c.code.toString(16).padStart(4, '0')) else append(c)
            }
        }
        append('"')
    }

    private fun enc(s: String): String = buildString {
        for (b in s.encodeToByteArray()) {
            val c = b.toInt().toChar()
            if (b >= 0 && (c.isLetterOrDigit() || c == '-' || c == '_' || c == '.' || c == '~')) append(c)
            else append('%').append((b.toInt() and 0xFF).toString(16).uppercase().padStart(2, '0'))
        }
    }
}
